import React from "react";
import communityPhoto from "../images/about/futurelab-programme-community.webp";
import workshopPhoto from "../images/about/futurelab-early-workshop.webp";
import herizonPhoto from "../images/about/modec-herizon-programme.webp";
import {
  BuildingIcon,
  ChartIcon,
  CheckIcon,
  CompassIcon,
  EarthIcon,
  HeartHandshakeIcon,
  NetworkIcon,
  SparklesIcon,
  UsersRoundIcon,
} from "./landing/Icons";
import { CASE_STUDIES, FEATURED_STUDY } from "./landing/content";
import { LeadFormProvider } from "./landing/lead-form/LeadFormProvider";
import { PRIORITY_ATTRS } from "./landing/platform/ProductShot";
import StatsStrip from "./landing/sections/StatsStrip";
import {
  CONTAINER,
  GRID_BACKDROP,
  PrimaryLink,
  Section,
  SectionHeading,
  SectionLabel,
  SecondaryLink,
} from "./landing/ui";

const DIFFERENCES = [
  {
    Icon: NetworkIcon,
    title: "Technology that supports the whole journey",
    body: "Programme setup, learning journeys, matching, scheduling, engagement tracking and reporting all work together.",
  },
  {
    Icon: HeartHandshakeIcon,
    title: "Programme experience behind the product",
    body: "The platform is shaped by what it takes to launch cohorts, support participants and report to stakeholders.",
  },
  {
    Icon: EarthIcon,
    title: "A network built for regional relevance",
    body: "Bring your own mentors, work with ours, or combine both across Malaysia and Southeast Asia.",
  },
] as const;

const PRINCIPLES = [
  {
    Icon: UsersRoundIcon,
    title: "People before process",
    body: "Technology should make human connection easier, not turn mentoring into another admin system participants have to put up with.",
  },
  {
    Icon: CompassIcon,
    title: "Structure without rigidity",
    body: "Good programmes give people a clear path and still leave room for the conversations that matter most.",
  },
  {
    Icon: ChartIcon,
    title: "Evidence that helps programmes improve",
    body: "Measurement should help teams understand participation, progress and outcomes, not just produce a bigger activity report.",
  },
] as const;

const HERIZON = CASE_STUDIES.find((study) => study.name === "MODEC HERizon");
const OTHER_STUDIES = CASE_STUDIES.filter((study) => study !== HERIZON);

type Props = {
  platformUrl: string;
  consultationUrl: string;
};

export default function AboutPage({ platformUrl, consultationUrl }: Props) {
  return (
    <LeadFormProvider>
      <section className="tw-relative tw-bg-[#f4f8fc] tw-pb-24 tw-pt-24 sm:tw-pb-16 sm:tw-pt-10">
        <div aria-hidden className="tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-40" style={GRID_BACKDROP} />

        <div
          className={`${CONTAINER} tw-relative tw-grid tw-grid-cols-[1fr_.88fr] tw-items-center tw-gap-12 sm:tw-grid-cols-1`}
        >
          <div className="tw-min-w-0">
            <p className="tw-m-0 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-[#b9d9f1] tw-bg-white tw-px-4 tw-py-2 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.14em] tw-text-[#0068b8] tw-shadow-sm">
              <SparklesIcon aria-hidden className="tw-h-4 tw-w-4" />
              About FutureLab
            </p>
            {/* The approved H1, mirrored in static_pages/about.html.erb; keep both in step. */}
            <h1 className="tw-mt-6 tw-mb-0 tw-max-w-4xl tw-text-[clamp(2.75rem,5.1vw,4.5rem)] sm:tw-text-[clamp(2.25rem,8vw,2.75rem)] tw-font-black tw-leading-[1.02] tw-tracking-[-.05em] tw-text-[#10233f]">
              A mentoring and coaching platform built to make good guidance easier to access.
            </h1>
            <p className="tw-mt-6 tw-mb-0 tw-max-w-2xl tw-text-xl tw-leading-8 tw-text-[#536a82] sm:tw-text-lg">
              FutureLab is a mentoring and coaching platform and programme partner for HR and L&amp;D
              teams in Malaysia and Southeast Asia. We turn informal introductions into structured
              development journeys that are easier to run, more useful to participants and clearer for
              leadership to measure.
            </p>

            <div className="tw-mt-8 tw-flex tw-flex-row tw-gap-3 sm:tw-flex-col">
              <PrimaryLink href={platformUrl}>Explore the platform</PrimaryLink>
              <SecondaryLink href={consultationUrl} data-lead-form="consultation">
                Talk to us
              </SecondaryLink>
            </div>
          </div>

          <div className="tw-relative tw-mx-auto tw-w-full tw-min-w-0 tw-max-w-[560px]">
            <div aria-hidden className="tw-pointer-events-none tw-absolute -tw-left-10 tw-top-20 tw-h-40 tw-w-40 tw-rounded-full tw-bg-[#72d7cc]/30 tw-blur-3xl" />
            <div aria-hidden className="tw-pointer-events-none tw-absolute -tw-right-8 -tw-top-8 tw-h-44 tw-w-44 tw-rounded-full tw-bg-[#78c8ff]/35 tw-blur-3xl" />

            <figure className="tw-relative tw-m-0 tw-overflow-hidden tw-rounded-[30px] tw-border tw-border-[#bdd8ea] tw-bg-white tw-p-3 tw-shadow-[0_30px_80px_rgba(23,72,111,.16)] xs:tw-p-2">
              <div className="tw-relative tw-aspect-[4/3] tw-overflow-hidden tw-rounded-[23px] tw-bg-[#dfeaf3]">
                <img
                  src={communityPhoto}
                  alt="FutureLab with mentors and participants at a social entrepreneur accelerator"
                  width={1600}
                  height={1205}
                  {...PRIORITY_ATTRS}
                  className="tw-block tw-h-full tw-w-full tw-object-cover"
                />
                <div className="tw-absolute tw-inset-x-0 tw-bottom-0 tw-bg-gradient-to-t tw-from-[#10233f]/95 tw-via-[#10233f]/55 tw-to-transparent tw-px-6 tw-pb-6 tw-pt-16 tw-text-white xs:tw-px-5 xs:tw-pb-5">
                  <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#8fe6dc]">
                    People make the programme
                  </p>
                  <p className="tw-mt-2 tw-mb-0 tw-max-w-md tw-text-xl tw-font-black tw-leading-snug xs:tw-text-lg">
                    Technology gives every mentoring relationship the structure to move forward.
                  </p>
                </div>
              </div>
              <figcaption className="tw-px-4 tw-py-3 tw-text-sm tw-leading-6 tw-text-[#60768c] xs:tw-px-3">
                Mentors, participants and programme teams working towards a shared goal.
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <StatsStrip />

      <Section>
        <div className="tw-grid tw-grid-cols-[.75fr_1.25fr] tw-items-start tw-gap-12 sm:tw-grid-cols-1">
          <div className="tw-min-w-0">
            <SectionLabel>Why FutureLab exists</SectionLabel>
            <SectionHeading>The right conversation can change a career.</SectionHeading>
            <figure className="tw-mt-8 tw-mb-0 tw-overflow-hidden tw-rounded-[24px] tw-border tw-border-[#d4e2ee] tw-bg-[#f4f8fc] tw-p-2">
              <img
                src={workshopPhoto}
                alt="A FutureLab facilitator leading an early mentoring workshop in Malaysia"
                width={1080}
                height={1080}
                loading="lazy"
                decoding="async"
                className="tw-block tw-aspect-square tw-h-auto tw-w-full tw-rounded-[18px] tw-object-cover"
              />
              <figcaption className="tw-px-3 tw-py-3 tw-text-sm tw-leading-6 tw-text-[#60768c]">
                An early FutureLab programme: access to guidance, shared learning and practical support.
              </figcaption>
            </figure>
          </div>

          <div className="tw-min-w-0 tw-space-y-6 tw-text-lg tw-leading-8 tw-text-[#60768c]">
            <p className="tw-m-0">
              FutureLab started with a simple idea: people make better decisions when they can learn
              from someone who has already been where they&rsquo;re going. So we made relevant industry
              mentors easier to reach.
            </p>
            <p className="tw-m-0">
              Working with employers, universities and public-sector programmes, we saw the bigger
              problem. Organisations believed in mentoring, but launching cohorts, matching the right
              people, keeping them engaged and showing results took too much manual work from HR and
              L&amp;D teams.
            </p>
            <div className="tw-border-0 tw-border-t tw-border-solid tw-border-[#d4e2ee] tw-pt-6">
              <SectionLabel>Today</SectionLabel>
              <p className="tw-mt-3 tw-mb-0 tw-text-2xl tw-font-black tw-leading-snug tw-tracking-[-.02em] tw-text-[#10233f] xs:tw-text-xl">
                We combine mentoring software, programme expertise and a regional mentor network, so
                structured mentoring and coaching is easier to deliver and to measure at scale.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <section className="tw-bg-[#10233f] tw-py-28 tw-text-white sm:tw-py-20">
        <div className={CONTAINER}>
          <div className="tw-grid tw-grid-cols-[.8fr_1.2fr] tw-items-end tw-gap-8 sm:tw-grid-cols-1">
            <div>
              <SectionLabel tone="mint">What makes us different</SectionLabel>
              <SectionHeading>Built from both sides of the programme.</SectionHeading>
            </div>
            <p className="tw-m-0 tw-max-w-2xl tw-justify-self-end tw-text-lg tw-leading-8 tw-text-[#b7cadd] sm:tw-justify-self-start">
              We know what participants go through, and what the team running the programme is up
              against.
            </p>
          </div>

          <div className="tw-mt-12 tw-grid tw-grid-cols-3 tw-gap-5 sm:tw-grid-cols-1">
            {DIFFERENCES.map(({ Icon, title, body }) => (
              <article key={title} className="tw-rounded-[26px] tw-border tw-border-white/15 tw-bg-white/[.06] tw-p-7">
                <span
                  aria-hidden
                  className="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-[#70d9ce] tw-text-[#10233f]"
                >
                  <Icon className="tw-h-6 tw-w-6" />
                </span>
                <h3 className="tw-mt-6 tw-mb-0 tw-text-2xl tw-font-black tw-leading-tight tw-tracking-[-.03em]">
                  {title}
                </h3>
                <p className="tw-mt-4 tw-mb-0 tw-leading-7 tw-text-[#b7cadd]">{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <Section className="tw-bg-[#f4f8fc]">
        <div className="tw-grid tw-grid-cols-[.82fr_1.18fr] tw-items-end tw-gap-10 sm:tw-grid-cols-1">
          <div>
            <SectionLabel>Experience you can build on</SectionLabel>
            <SectionHeading>Programme knowledge, earned through delivery.</SectionHeading>
          </div>
          <p className="tw-m-0 tw-max-w-2xl tw-justify-self-end tw-text-lg tw-leading-8 tw-text-[#60768c] sm:tw-justify-self-start">
            Leadership, career development, entrepreneurship and workplace-readiness programmes, for
            companies, universities and public institutions.
          </p>
        </div>

        <div className="tw-mt-12 tw-grid tw-grid-cols-2 tw-gap-5 sm:tw-grid-cols-1">
          <article className="tw-rounded-[28px] tw-bg-[#087bd3] tw-p-9 tw-text-white xs:tw-p-7">
            <p className="tw-m-0 tw-text-sm tw-font-black tw-uppercase tw-tracking-[.14em] tw-text-[#d9efff]">
              {FEATURED_STUDY.name} · Corporate mentoring
            </p>
            <p className="tw-mt-8 tw-mb-0 tw-text-5xl tw-font-black tw-leading-[1.05] tw-tracking-[-.05em]">
              {FEATURED_STUDY.headline[0]}
              <br />
              {FEATURED_STUDY.headline[1]}
            </p>
            <p className="tw-mt-6 tw-mb-0 tw-max-w-lg tw-text-lg tw-leading-8 tw-text-white">{FEATURED_STUDY.body}</p>
          </article>

          <div className="tw-grid tw-content-start tw-gap-4">
            {OTHER_STUDIES.map((study) => (
              <article
                key={study.name}
                className="tw-grid tw-grid-cols-[180px_1fr] tw-gap-6 tw-rounded-2xl tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-6 xs:tw-grid-cols-1 xs:tw-gap-2"
              >
                <h3 className="tw-m-0 tw-text-base tw-font-black tw-text-[#1a3958]">{study.name}</h3>
                <p className="tw-m-0 tw-leading-7 tw-text-[#60768c]">{study.body}</p>
              </article>
            ))}
          </div>
        </div>

        {HERIZON ? (
          <article className="tw-mt-5 tw-grid tw-grid-cols-[.72fr_1.28fr] tw-overflow-hidden tw-rounded-[28px] tw-border tw-border-[#c8deec] tw-bg-white sm:tw-grid-cols-1">
            <div className="tw-min-h-[360px] tw-overflow-hidden tw-bg-[#dfeaf3] sm:tw-max-h-[520px]">
              <img
                src={herizonPhoto}
                alt="FutureLab and MODEC colleagues at the HERizon women's leadership programme"
                width={1200}
                height={1594}
                loading="lazy"
                decoding="async"
                className="tw-block tw-h-full tw-w-full tw-object-cover tw-object-[50%_68%]"
              />
            </div>
            <div className="tw-flex tw-flex-col tw-justify-center tw-p-12 sm:tw-p-10 xs:tw-p-7">
              <SectionLabel>{HERIZON.name}</SectionLabel>
              <h3 className="tw-mt-4 tw-mb-0 tw-text-4xl tw-font-black tw-leading-tight tw-tracking-[-.035em] tw-text-[#1a3958] xs:tw-text-3xl">
                Structured mentoring for women&rsquo;s leadership development.
              </h3>
              <p className="tw-mt-5 tw-mb-0 tw-text-lg tw-leading-8 tw-text-[#60768c]">
                We supported HERizon with mentoring and development activities that helped participants
                build confidence, strengthen their leadership and learn from people with relevant
                experience.
              </p>
            </div>
          </article>
        ) : null}

        <div className="tw-mt-5 tw-flex tw-flex-row tw-items-center tw-justify-between tw-gap-4 tw-rounded-2xl tw-border tw-border-[#c8deec] tw-bg-white tw-p-6 xs:tw-flex-col xs:tw-items-start">
          <div className="tw-flex tw-items-start tw-gap-4">
            <BuildingIcon aria-hidden className="tw-mt-1 tw-h-6 tw-w-6 tw-shrink-0 tw-text-[#087bd3]" />
            <div>
              <p className="tw-m-0 tw-font-black tw-text-[#1a3958]">Recognised in workplace learning</p>
              <p className="tw-mt-1 tw-mb-0 tw-leading-7 tw-text-[#60768c]">
                Named in the HolonIQ Southeast Asia EdTech 50 every year from 2020 to 2025.
              </p>
            </div>
          </div>
          <span className="tw-shrink-0 tw-rounded-full tw-bg-[#eaf6fd] tw-px-4 tw-py-2 tw-text-sm tw-font-black tw-text-[#0068b8]">
            Malaysia · Southeast Asia
          </span>
        </div>
      </Section>

      <Section>
        <div className="tw-mx-auto tw-max-w-3xl tw-text-center">
          <SectionLabel>How we think about the work</SectionLabel>
          <SectionHeading>Human connection, supported by better infrastructure.</SectionHeading>
        </div>

        <div className="tw-mt-12 tw-grid tw-grid-cols-3 tw-gap-5 sm:tw-grid-cols-1">
          {PRINCIPLES.map(({ Icon, title, body }) => (
            <article key={title} className="tw-rounded-[26px] tw-border tw-border-[#d4e2ee] tw-bg-white tw-p-7">
              <Icon aria-hidden className="tw-h-7 tw-w-7 tw-text-[#087bd3]" />
              <h3 className="tw-mt-5 tw-mb-0 tw-text-xl tw-font-black tw-text-[#1a3958]">{title}</h3>
              <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#60768c]">{body}</p>
            </article>
          ))}
        </div>

        <div className="tw-mt-12 tw-grid tw-grid-cols-[.75fr_1.25fr] tw-items-center tw-gap-6 tw-rounded-[30px] tw-border tw-border-[#b9daef] tw-bg-[#eef7fd] tw-p-12 sm:tw-grid-cols-1 sm:tw-p-10 xs:tw-p-7">
          <div>
            <SectionLabel>Our mission</SectionLabel>
            <h2 className="tw-mt-4 tw-mb-0 tw-text-4xl tw-font-black tw-leading-[1.08] tw-tracking-[-.04em] xs:tw-text-3xl">
              Make relevant guidance easier to access when it matters most.
            </h2>
          </div>
          <div className="tw-pl-8 sm:tw-pl-0">
            <p className="tw-m-0 tw-text-lg tw-leading-8 tw-text-[#536a82]">
              We build the tools that help organisations create more useful mentoring and coaching
              relationships, so more people can learn from experience, handle change and move forward
              with confidence.
            </p>
            <p className="tw-mt-5 tw-mb-0 tw-flex tw-gap-3 tw-text-sm tw-font-bold tw-leading-6 tw-text-[#304d68]">
              <CheckIcon aria-hidden className="tw-mt-1 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#0f7a62]" />
              Designed in Malaysia for organisations building talent across the region.
            </p>
          </div>
        </div>
      </Section>
    </LeadFormProvider>
  );
}
